import { ButtonPopup } from "../../ui";
import { BotonRenovarCss, CardsBlockCss } from "../../styles";
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { PopupBaja } from "../../popup";
import { VITE_BACKEND_URL } from '@/api/config';
import { safeFetch } from "@/api/safeFetch";


type EstadoSuscripcion = 0 | 1 | 2;

// Define la estructura de cada profesión
type ProfesionItem = {
  tiempoRestante?: string; // se mostrará como hh:mm:ss
  segundosRestantes?: number; // 🔹 calculado automáticamente
  esta_activa?: EstadoSuscripcion; // 0 = pausada/inactiva, 1 = activa, 2 = por renovar
  profesion_id: number;
  perfil_profesional_id: number;
  profesional_id: number;
  nombre_profesion: string;
  perfil_foto: string;
  profesion_imagen: string;
  suscripcion_fin?: string; // ISO string con fecha de fin
  expirada?: boolean;
};

const ListadoDeProfesiones: React.FC = () => {
  const { profesionalId } = useParams<{ profesionalId: string }>();
  const [profesiones, setProfesiones] = useState<ProfesionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [showPopup, setShowPopup] = useState(false);
  const [perfilSeleccionado, setPerfilSeleccionado] = useState<ProfesionItem | null>(null);

  const buildImageUrl = (path?: string): string => {
    if (!path) return "/img/logo/LogoLinkServs.png";
    if (path.startsWith("http") || path.startsWith("data:image") || path.startsWith("blob:")) return path;
    return `${VITE_BACKEND_URL}${path.startsWith("/") ? path : `/${path}`}`;
  };

  // 🔹 Fetch profesiones
  const fetchProfesiones = async () => {
    if (!profesionalId) return;
    setProfesiones([]);
    setError(null);
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No hay token disponible");

      const res = await safeFetch(`/api/profesionales/${profesionalId}/profesiones`, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });

      if (res.status === 403) { navigate("/"); return; }

      if (!res.ok) {
        if (res.status === 404) { setProfesiones([]); return; }
        throw new Error(`HTTP ${res.status}`);
      }
      const data: ProfesionItem[] = await res.json();

      setProfesiones(actualizarTiempoRestante(data));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProfesiones(); }, [profesionalId, navigate]);

  // 🔹 Actualizar tiempo restante
  const actualizarTiempoRestante = (profesiones: ProfesionItem[]): ProfesionItem[] => {
    const ahora = new Date().getTime();

    return profesiones.map(p => {
      if (!p.suscripcion_fin) {
        return { ...p, segundosRestantes: 0, tiempoRestante: "Expirada", expirada: true };
      }

      const fin = new Date(p.suscripcion_fin).getTime();
      if (isNaN(fin)) {
        return { ...p, segundosRestantes: 0, tiempoRestante: "Expirada", expirada: true };
      }

      let segundos = Math.max(Math.floor((fin - ahora) / 1000), 0);

      // Si ya llegó a 0 → marcar como expirada
      const expirada = segundos === 0;

      let tiempoStr = "";
      if (segundos >= 86400) {
        const dias = Math.floor(segundos / 86400);
        const horasRestantes = Math.floor((segundos % 86400) / 3600);
        tiempoStr = `${dias}d ${horasRestantes}h`;
      } else {
        const horas = Math.floor(segundos / 3600);
        const minutos = Math.floor((segundos % 3600) / 60);
        const segs = segundos % 60;
        tiempoStr = `${horas.toString().padStart(2, "0")}h ${minutos.toString().padStart(2, "0")}m ${segs.toString().padStart(2, "0")}s`;
      }

      return { ...p, segundosRestantes: segundos, tiempoRestante: tiempoStr, expirada };
    });
  };



  // 🔹 Actualizar tiempo restante cada segundo
  useEffect(() => {
    const intervalo = setInterval(() => {
      setProfesiones(prev =>
        prev.map(p => {
          const segundos = Math.max((p.segundosRestantes ?? 0) - 1, 0);

          let tiempoStr = "";

          const dias = Math.floor(segundos / 86400);
          const horas = Math.floor((segundos % 86400) / 3600);
          const minutos = Math.floor((segundos % 3600) / 60);
          const segs = segundos % 60;

          if (dias > 0) {
            tiempoStr =
              `${dias}d ` +
              `${horas.toString().padStart(2, "0")}h ` +
              `${minutos.toString().padStart(2, "0")}m ` +
              `${segs.toString().padStart(2, "0")}s`;
          } else {
            tiempoStr =
              `${horas.toString().padStart(2, "0")}h ` +
              `${minutos.toString().padStart(2, "0")}m ` +
              `${segs.toString().padStart(2, "0")}s`;
          }

          return {
            ...p,
            segundosRestantes: segundos,
            tiempoRestante: segundos === 0 ? "Expirada" : tiempoStr,
            expirada: segundos === 0
          };
        })
      );
    }, 1000);

    return () => clearInterval(intervalo);
  }, []);

  /*   const mostrarRenovar = (item: ProfesionItem) => item.esta_activa === 2; */
  /*  const mostrarActivo = (item: ProfesionItem) => item.esta_activa === 1; */

  // 🔹 Funciones de pausa y reanudación
  const pauseSubscription = async (perfil: ProfesionItem) => {
    const token = localStorage.getItem("token");
    if (!token) throw new Error("No hay token disponible");

    const res = await safeFetch(`/api/pauseSuscripcion`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ perfil_profesional_id: perfil.perfil_profesional_id }),
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `HTTP ${res.status}`);
    }

    await fetchProfesiones();
    setShowPopup(false);
    setPerfilSeleccionado(null);
  };

  const resumeSubscription = async (perfil: ProfesionItem) => {
    const token = localStorage.getItem("token");
    if (!token) throw new Error("No hay token disponible");

    const res = await safeFetch(`/api/resumeSuscripcion`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ perfil_profesional_id: perfil.perfil_profesional_id }),
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `HTTP ${res.status}`);
    }

    await fetchProfesiones();
  };


  if (loading) return <p>Cargando profesiones...</p>;
  if (error) return <p className="text-red-600">Error: {error}</p>;
  if (profesiones.length === 0)
    return (
      <div className="w-full max-w-xl h-40 flex items-center justify-center mx-auto mt-10 border border-zinc-300 rounded-xl shadow-lg bg-white text-center px-6 text-gray-700 text-lg font-semibold relative opacity-0 animate-fadeIn" style={{ animationFillMode: "forwards", animationDuration: "1s" }}>
        <p>
          ¡Estamos trabajando en ello!<br />
          Muy pronto tendrás <strong className="text-indigo-600">servicios</strong> disponibles por aquí.
        </p>
      </div>
    );

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 w-full max-w-screen-xl mx-auto place-items-stretch mt-20 px-8">
        {profesiones.map((item) => {
          const estaExpirada = item.expirada;
          const estaPausada = item.esta_activa === 0;
          const mostrarNiebla = estaExpirada || estaPausada;

          return (
            <div
              key={item.perfil_profesional_id}
              className={`${CardsBlockCss.profesion} rounded-2xl overflow-hidden bg-zinc-600 flex flex-col justify-between`}
              style={{ height: "100%" }}
            >

              {/* CONTENIDO DE LA CARD */}
              {(item.esta_activa === 1 || item.esta_activa === 2) && !estaExpirada ? (
                <Link
                  to={`/editProfesional/${profesionalId}/${item.profesion_id}/${item.perfil_profesional_id}`}
                  className="block w-full flex-grow relative group"
                >
                  <div className="w-full aspect-square overflow-hidden relative">

                    {/* IMAGEN */}
                    <img
                      src={buildImageUrl(item.perfil_foto)}
                      alt={item.nombre_profesion}
                      className={`w-full h-full object-cover object-center ${mostrarNiebla ? "opacity-40" : ""}`}
                      onError={(e) => (e.currentTarget.src = "/img/logo/LogoLinkServs.png")}
                    />

                    {/* NIEBLA */}
                    {mostrarNiebla && (
                      <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                        <span className="text-white text-2xl font-bold">
                          {estaExpirada ? "Expirada" : "Pausada"}
                        </span>
                      </div>
                    )}

                    {/* CONTADOR (solo si está por renovar y no expirada) */}
                    {/* CONTADOR SIEMPRE */}
                    {!estaExpirada && item.tiempoRestante && (
                      <span
                        className={`
                           absolute top-2 right-2 text-xs font-bold px-2 py-1 rounded-md shadow
                           ${(item.segundosRestantes ?? 0) <= 30 * 24 * 60 * 60
                            ? "bg-red-600 text-white animate-pulse"
                            : item.esta_activa === 1
                              ? "bg-green-600 text-white"
                              : "bg-yellow-400 text-black"
                          }
                        `}
                      >
                        {item.tiempoRestante}
                      </span>
                    )}

                    {/* HOVER EDITAR */}
                    {!mostrarNiebla && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition">
                        <span className="text-white text-2xl font-bold">Editar</span>
                      </div>
                    )}
                  </div>

                  <div className="p-2 flex flex-col items-center justify-center border-t-2">
                    <h2 className="text-center text-md font-semibold">{item.nombre_profesion}</h2>
                  </div>
                </Link>
              ) : (
                /* CARD INACTIVA O EXPIRADA */
                <div className="block w-full flex-grow relative opacity-60 cursor-not-allowed">
                  <div className="w-full aspect-square overflow-hidden relative">
                    <img
                      src={buildImageUrl(item.perfil_foto)}
                      alt={item.nombre_profesion}
                      className="w-full h-full object-cover object-center"
                      onError={(e) => (e.currentTarget.src = "/img/logo/LogoLinkServs.png")}
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40">
                      <span className="text-white text-2xl font-bold">
                        {estaExpirada ? "Expirada" : "Inactiva"}
                      </span>
                    </div>
                  </div>

                  <div className="p-2 flex flex-col items-center justify-center border-t-2">
                    <h2 className="text-center text-md font-semibold">{item.nombre_profesion}</h2>
                  </div>
                </div>
              )}

              {/* BOTONES */}
              <div className="flex items-center mt-2 pb-3 px-2 justify-center">

                {/* EXPIRADA → SOLO RENOVAR */}
                {estaExpirada && (
                  <ButtonPopup
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/renovar/${item.profesional_id}/${item.perfil_profesional_id}`);
                    }}
                    className="!rounded-xl border-2 border-red-500 w-32 h-10 md:w-40"
                  >
                    Renovar
                  </ButtonPopup>
                )}

                {/* POR RENOVAR (<=30 días) */}
                {!estaExpirada && item.esta_activa === 2 && (
                  <ButtonPopup
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/renovar/${item.profesional_id}/${item.perfil_profesional_id}`);
                    }}
                    className={`!rounded-xl border-2 border-red-500 ${BotonRenovarCss.borderPulse} w-32 h-10 md:w-40`}
                  >
                    Renovar
                  </ButtonPopup>
                )}

                {/* ACTIVA (>30 días) */}
                {item.esta_activa === 1 && (
                  <ButtonPopup
                    onClick={(e) => {
                      e.stopPropagation();
                      setPerfilSeleccionado(item);
                      setShowPopup(true);
                    }}
                    className="!rounded-xl border-2 border-blue-700 w-32 h-10 md:w-40"
                  >
                    Pausar
                  </ButtonPopup>
                )}

                {/* PAUSADA */}
                {item.esta_activa === 0 && (
                  <ButtonPopup
                    onClick={(e) => {
                      e.stopPropagation();
                      resumeSubscription(item);
                    }}
                    className="!rounded-xl border-2 border-green-700 w-32 h-10 md:w-40"
                  >
                    Reanudar
                  </ButtonPopup>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* POPUP PAUSAR */}
      {showPopup && perfilSeleccionado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <PopupBaja
            onConfirm={() => pauseSubscription(perfilSeleccionado)}
            onCancel={() => {
              setShowPopup(false);
              setPerfilSeleccionado(null);
            }}
          />
        </div>
      )}

    </>
  );
};

export default ListadoDeProfesiones;